/**
 * Reusable pause-menu button chip — parchment background, cyan UGC corner
 * mark, focusable + clickable. Used by RunnerScene's pause overlay to expose
 * the AUTHOR SIDEQUEST and MARKET buttons (Variant B only).
 */

import Phaser from "phaser";

import { PALETTE, UGC_CYAN } from "../config";

const WIDTH = 140;
const HEIGHT = 22;

export class PauseMenuButton {
  readonly container: Phaser.GameObjects.Container;
  private readonly bg: Phaser.GameObjects.Rectangle;
  private readonly label: Phaser.GameObjects.Text;
  private focused = false;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    labelText: string,
    onActivate: () => void,
    width = WIDTH,
  ) {
    this.container = scene.add.container(x, y).setDepth(40);

    this.bg = scene.add
      .rectangle(0, 0, width, HEIGHT, PALETTE.pauseButtonBg, 0.95)
      .setOrigin(0.5, 0.5)
      .setStrokeStyle(1, PALETTE.pauseButtonEdge);

    this.label = scene.add
      .text(0, 0, labelText, {
        fontFamily: "monospace",
        fontSize: "9px",
        color: `#${PALETTE.pauseButtonText.toString(16).padStart(6, "0")}`,
        fontStyle: "bold",
      })
      .setOrigin(0.5, 0.5);

    const corner = scene.add
      .rectangle(-width / 2 + 2, -HEIGHT / 2 + 2, 3, 3, UGC_CYAN, 1)
      .setOrigin(0, 0);

    this.container.add([this.bg, this.label, corner]);

    this.bg.setInteractive({ useHandCursor: true });
    this.bg.on("pointerdown", onActivate);
    this.bg.on("pointerover", () => this.setFocused(true));
    this.bg.on("pointerout", () => this.setFocused(false));

    this.paint();
  }

  setFocused(focused: boolean): void {
    if (this.focused === focused) return;
    this.focused = focused;
    this.paint();
  }

  setVisible(v: boolean): void {
    this.container.setVisible(v);
  }

  destroy(): void {
    this.container.destroy();
  }

  static readonly WIDTH = WIDTH;
  static readonly HEIGHT = HEIGHT;

  private paint(): void {
    if (this.focused) {
      this.bg.setStrokeStyle(2, UGC_CYAN);
      this.bg.setFillStyle(PALETTE.pauseButtonBg, 1);
    } else {
      this.bg.setStrokeStyle(1, PALETTE.pauseButtonEdge);
      this.bg.setFillStyle(PALETTE.pauseButtonBg, 0.95);
    }
  }
}
